import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

export interface EmailPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    previewHtml: string;
    previewLoading: boolean;
}

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
    isOpen,
    onClose,
    previewHtml,
    previewLoading,
}) => {
    const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');

    // Reset view mode to desktop whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setViewMode('desktop');
        }
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-[#3F5265]">Preview Email</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Close preview"
                    >
                        <FiXComponent size={24} />
                    </button>
                </div>

                {/* View Toggle */}
                <div className="flex justify-center items-center p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center bg-[#E8F4F6] rounded-lg p-1 gap-1">
                        <button
                            onClick={() => setViewMode('desktop')}
                            className={`p-2.5 rounded-md transition-colors ${viewMode === 'desktop'
                                    ? 'bg-white text-[#00899E] shadow-sm'
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                            title="Desktop View"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                            </svg>
                        </button>
                        <button
                            onClick={() => setViewMode('mobile')}
                            className={`p-2.5 rounded-md transition-colors ${viewMode === 'mobile'
                                    ? 'bg-white text-[#00899E] shadow-sm'
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                            title="Mobile View"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                                <line x1="12" y1="18" x2="12.01" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Modal Body - Template Preview */}
                <div className="flex-1 p-4 overflow-auto bg-gray-100">
                    {previewLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]"></div>
                        </div>
                    ) : viewMode === 'desktop' ? (
                        <div className="w-full max-w-3xl mx-auto">
                            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                {previewHtml ? (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                        className="w-full"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-64 text-gray-400">
                                        No preview available
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center items-start">
                            <div
                                className="w-[375px] bg-white rounded-lg shadow-lg overflow-hidden"
                                style={{ height: 'fit-content', maxHeight: '80vh' }}
                            >
                                {previewHtml ? (
                                    <div
                                        style={{
                                            transform: 'scale(0.65)',
                                            transformOrigin: 'top center',
                                            width: '153.8%',
                                            marginLeft: '-26.9%',
                                        }}
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                                            className="w-full"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-64 text-gray-400">
                                        No preview available
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end p-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailPreviewModal;